import type { MapFeedItemDTO } from "../../data/types";
import { barSvg } from "../icons/barSvg";
import { formatInitials } from "../../utils/format";
import { venuePinZIndex } from "./pinZIndex";

type CreateVenuePinElOptions = {
  item: MapFeedItemDTO;
  venueId: string;
  getCurrentItem: (venueId: string) => MapFeedItemDTO | undefined;
  onSelect: (item: MapFeedItemDTO) => void;
};

/**
 * LIVE ärver rosa och puls från venue-pin--has-data. RECENT och TODAY slår
 * av pulsen och blir lila — allt som spelat något det senaste dygnet men
 * inte just nu ser likadant ut.
 *
 * Medvetet utan catch-all: ett läge vi inte känner igen ska inte ärva den
 * lila stilen utan bara få basklassen, som är osynlig. markerSync ska redan
 * ha filtrerat bort sådant, och blir det ändå fel ska det inte se ut som en
 * bar med musik igång.
 */
const PURPLE_STATUSES = new Set(["RECENT", "TODAY"]);

function getVenuePinClass(item: MapFeedItemDTO) {
  // Ställen utan betalplan eller påslagen sensor får en egen, mindre grå
  // pin. De har inget flöde, så status säger ingenting om dem.
  if (item.hasLiveData === false) {
    return "venue-pin venue-pin--no-live";
  }

  const statusClass =
    item.status === "LIVE"
      ? "venue-pin--live"
      : PURPLE_STATUSES.has(item.status)
      ? "venue-pin--recent"
      : "";

  return ["venue-pin", statusClass ? "venue-pin--has-data" : "", statusClass]
    .filter(Boolean)
    .join(" ");
}

/**
 * Avataren på den som är incheckad. Ligger som syskon till själva pinnen och
 * inte inuti den, eftersom pinnens innerHTML skrivs om vid varje uppdatering.
 *
 * Den är absolut positionerad och därmed utanför flödet, så markörens
 * ankarpunkt påverkas inte — pinnen sitter kvar på exakt samma koordinat.
 */
function updateCheckedInAvatar(rootEl: HTMLElement, item: MapFeedItemDTO) {
  const existing = rootEl.querySelector<HTMLElement>(".venue-pin-avatar");
  const checkedIn = item.checkedIn ?? null;

  if (!checkedIn) {
    existing?.remove();
    return;
  }

  const { latest, count } = checkedIn;

  const avatarEl = existing ?? document.createElement("div");

  if (!existing) {
    avatarEl.className = "venue-pin-avatar";
    rootEl.appendChild(avatarEl);
  }

  // Fler än en på stället ritas som en liten trave, utan siffra. Vilka det
  // är framgår när man öppnar venue-kortet.
  avatarEl.classList.toggle("venue-pin-avatar--stacked", count > 1);
  avatarEl.classList.toggle("venue-pin-avatar--self", latest.isSelf);

  avatarEl.title =
    count > 1
      ? `${latest.displayName} and ${count - 1} more are here`
      : `${latest.displayName} is here`;

  const nextKey = `${latest.id}:${latest.avatarUrl ?? ""}`;

  // Rör inte DOM:en när inget ändrats — annars laddas bilden om var 15:e
  // sekund när mapfeed pollar.
  if (avatarEl.dataset.avatarKey === nextKey) return;

  avatarEl.dataset.avatarKey = nextKey;

  if (latest.avatarUrl) {
    const img = document.createElement("img");
    img.src = latest.avatarUrl;
    img.alt = "";
    avatarEl.replaceChildren(img);
  } else {
    const span = document.createElement("span");
    span.textContent = formatInitials(latest.displayName).slice(0, 1);
    avatarEl.replaceChildren(span);
  }
}

export function updateVenuePinEl(rootEl: HTMLElement, item: MapFeedItemDTO) {
  const pinEl = rootEl.querySelector<HTMLElement>(".venue-pin-inner");

  if (!pinEl) return;

  // Ritordningen sätts på ROTELEMENTET, inte på pinnen inuti.
  //
  // Mapbox positionerar rotelementet absolut, så varje markör är sin egen
  // stapelkontext — ett z-index på ett barn ordnar ingenting mot andra
  // markörer. CSS-reglerna på .venue-pin--recent och .venue-pin--no-live
  // såg alltså rätt ut och gjorde ingenting, och gråa pins kunde hamna
  // ovanpå barer som spelade beroende på skapandeordningen.
  rootEl.style.zIndex = String(venuePinZIndex(item));

  const previousLastPlayedAt = rootEl.dataset.lastPlayedAt ?? "";
  const nextLastPlayedAt = item.lastPlayedAt ?? "";

  pinEl.className = `venue-pin-inner ${getVenuePinClass(item)}`;
  pinEl.innerHTML = barSvg(18);

  rootEl.style.cursor =
    item.nowPlaying || item.hasLiveData === false ? "pointer" : "default";

  const shouldAnimate =
    item.status === "LIVE" &&
    previousLastPlayedAt &&
    nextLastPlayedAt &&
    previousLastPlayedAt !== nextLastPlayedAt;

  if (shouldAnimate) {
    pinEl.classList.remove("venue-pin--just-updated");

    // Restart CSS animation if class already existed.
    void pinEl.offsetWidth;

    pinEl.classList.add("venue-pin--just-updated");
  }

  updateCheckedInAvatar(rootEl, item);

  rootEl.dataset.lastPlayedAt = nextLastPlayedAt;
}

export function createVenuePinEl({
  item,
  venueId,
  getCurrentItem,
  onSelect,
}: CreateVenuePinElOptions) {
  const rootEl = document.createElement("div");

  /*
    Important:
    Mapbox owns rootEl positioning.
    Do not set/replace rootEl.className after marker creation.
    Only update the child element.
  */
  const pinEl = document.createElement("div");
  pinEl.className = "venue-pin-inner";
  rootEl.appendChild(pinEl);

  updateVenuePinEl(rootEl, item);

  rootEl.addEventListener("click", (e) => {
    e.stopPropagation();

    const currentItem = getCurrentItem(venueId);

    // Gråa pins går att klicka på trots att de saknar låt — kortet
    // berättar då att live-data saknas.
    if (!currentItem?.nowPlaying && currentItem?.hasLiveData !== false) return;

    onSelect(currentItem);
  });

  return rootEl;
}
